import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { formatPhoneNumber } from "@/lib/phoneUtils";
import { geocodeAddress } from "@/lib/map-utils";
import { toast as sonnerToast } from "sonner";

interface EditClientSheetProps {
  isOpen: boolean;
  onClose: () => void;
  client: {
    _id: Id<"businesses">;
    name: string;
    address?: string;
    accountType?: string;
    serviceStatus?: "active" | "inactive" | "pending";
    contactName?: string;
    contactTitle?: string;
    contactEmail?: string;
    contactPhone?: string;
    coordinates?: { lat: number; lng: number };
  };
}

export function EditClientSheet({ isOpen, onClose, client }: EditClientSheetProps) {
  const [showCustomAccountType, setShowCustomAccountType] = useState(false);
  const [customAccountType, setCustomAccountType] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    contactName: "",
    contactTitle: "",
    contactEmail: "",
    contactPhone: "",
    accountType: "",
    address: "",
    status: "active",
  });
  const [isGeocoding, setIsGeocoding] = useState(false);

  // Replace manual geocoding with utility

  const updateClient = useMutation(api.businesses.update);

  // Initialize form data when client prop changes
  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name || "",
        contactName: client.contactName || "",
        contactTitle: client.contactTitle || "",
        contactEmail: client.contactEmail || "",
        contactPhone: client.contactPhone || "",
        accountType: client.accountType || "",
        address: client.address || "",
        status: client.serviceStatus || "active",
      });

      // Check if we need to show custom account type - check against all the standard types from CreateClientSheet
      const standardTypes = [
        "corporate-office",
        "professional-service",
        "co-working",
        "business-park",
        "office-complex",
        "k-12",
        "university",
        "private-academy",
        "vocational-school",
        "daycare",
        "hospital",
        "medical-clinic",
        "dental-office",
        "surgery-center",
        "physical-therapy",
        "long-term-care",
        "shopping-mall",
        "department-store",
        "boutique",
        "grocery-store",
        "big-box",
        "hotel",
        "resort",
        "convention-center",
        "banquet-hall",
        "restaurant",
        "bank",
        "credit-union",
        "investment-firm",
        "insurance-company",
        "manufacturing",
        "warehouse",
        "distribution-center",
        "food-processing",
        "factory",
        "church",
        "synagogue",
        "mosque",
        "religious-education",
        "municipal-office",
        "courthouse",
        "post-office",
        "dmv",
        "public-library",
        "movie-theater",
        "concert-hall",
        "sports-arena",
        "museum",
        "art-gallery",
        "airport",
        "bus-terminal",
        "train-station",
        "subway-station",
        "apartment-complex",
        "condominium",
        "homeowners-association",
      ];

      if (client.accountType && !standardTypes.includes(client.accountType)) {
        setShowCustomAccountType(true);
        setCustomAccountType(client.accountType);
      }
    }
  }, [client]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSelectChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const formattedPhone = formatPhoneNumber(value);
    setFormData((prev) => ({
      ...prev,
      [id]: formattedPhone,
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsGeocoding(true);
      let coordinates = client.coordinates;

      if (formData.address && formData.address !== client.address) {
        coordinates = (await geocodeAddress(formData.address)) || coordinates;
      }

      // Use the actual account type if custom is selected
      const finalAccountType = showCustomAccountType ? customAccountType : formData.accountType;

      // Update the client in the database
      await updateClient({
        id: client._id,
        name: formData.name,
        address: formData.address,
        accountType: finalAccountType,
        serviceStatus: formData.status as "active" | "inactive" | "pending",
        contactName: formData.contactName,
        contactTitle: formData.contactTitle,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        coordinates: coordinates || undefined,
      });

      sonnerToast.success("Client updated successfully");
      onClose();
    } catch (error) {
      console.error("Error updating client:", error);
      sonnerToast.error("Failed to update client");
    } finally {
      setIsGeocoding(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Edit Client</SheetTitle>
          <SheetDescription>Update the details for this client.</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="name">Company Name</Label>
              <Input
                id="name"
                placeholder="Enter company name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactName">Contact Name</Label>
                <Input
                  id="contactName"
                  placeholder="Full name"
                  value={formData.contactName}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactTitle">Job Title</Label>
                <Input
                  id="contactTitle"
                  placeholder="Job title"
                  value={formData.contactTitle}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactEmail">Email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  placeholder="Email address"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactPhone">Phone</Label>
                <Input
                  id="contactPhone"
                  placeholder="(555) 123-4567"
                  value={formData.contactPhone}
                  onChange={handlePhoneChange}
                  maxLength={14}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                placeholder="Physical address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="accountType">Account Type</Label>
              <Select
                value={showCustomAccountType ? "custom" : formData.accountType}
                onValueChange={(value) => {
                  setShowCustomAccountType(value === "custom");
                  handleSelectChange("accountType", value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectGroup>
                    <SelectLabel>Commercial Office Building</SelectLabel>
                    <SelectItem value="corporate-office">Corporate office</SelectItem>
                    <SelectItem value="professional-service">Professional service firm</SelectItem>
                    <SelectItem value="co-working">Co-working space</SelectItem>
                    <SelectItem value="business-park">Business park</SelectItem>
                    <SelectItem value="office-complex">Office complex</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Educational Institution</SelectLabel>
                    <SelectItem value="k-12">K-12 school</SelectItem>
                    <SelectItem value="university">University or college</SelectItem>
                    <SelectItem value="private-academy">Private academy</SelectItem>
                    <SelectItem value="vocational-school">Vocational school</SelectItem>
                    <SelectItem value="daycare">Daycare center</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Healthcare Facility</SelectLabel>
                    <SelectItem value="hospital">Hospital</SelectItem>
                    <SelectItem value="medical-clinic">Medical clinic</SelectItem>
                    <SelectItem value="dental-office">Dental office</SelectItem>
                    <SelectItem value="surgery-center">Outpatient surgery center</SelectItem>
                    <SelectItem value="physical-therapy">Physical therapy center</SelectItem>
                    <SelectItem value="long-term-care">Long-term care facility</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Retail Space</SelectLabel>
                    <SelectItem value="shopping-mall">Shopping mall</SelectItem>
                    <SelectItem value="department-store">Department store</SelectItem>
                    <SelectItem value="boutique">Boutique</SelectItem>
                    <SelectItem value="grocery-store">Grocery store</SelectItem>
                    <SelectItem value="big-box">Big box retailer</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Hospitality Industry</SelectLabel>
                    <SelectItem value="hotel">Hotel or motel</SelectItem>
                    <SelectItem value="resort">Resort</SelectItem>
                    <SelectItem value="convention-center">Convention center</SelectItem>
                    <SelectItem value="banquet-hall">Banquet hall</SelectItem>
                    <SelectItem value="restaurant">Restaurant or cafe</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Financial Institution</SelectLabel>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="credit-union">Credit union</SelectItem>
                    <SelectItem value="investment-firm">Investment firm</SelectItem>
                    <SelectItem value="insurance-company">Insurance company</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Industrial Facility</SelectLabel>
                    <SelectItem value="manufacturing">Manufacturing plant</SelectItem>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="distribution-center">Distribution center</SelectItem>
                    <SelectItem value="food-processing">Food processing facility</SelectItem>
                    <SelectItem value="factory">Factory</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Religious Facility</SelectLabel>
                    <SelectItem value="church">Church</SelectItem>
                    <SelectItem value="synagogue">Synagogue</SelectItem>
                    <SelectItem value="mosque">Mosque</SelectItem>
                    <SelectItem value="religious-education">Religious education center</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Government Building</SelectLabel>
                    <SelectItem value="municipal-office">Municipal office</SelectItem>
                    <SelectItem value="courthouse">Courthouse</SelectItem>
                    <SelectItem value="post-office">Post office</SelectItem>
                    <SelectItem value="dmv">DMV location</SelectItem>
                    <SelectItem value="public-library">Public library</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Entertainment Venue</SelectLabel>
                    <SelectItem value="movie-theater">Movie theater</SelectItem>
                    <SelectItem value="concert-hall">Concert hall</SelectItem>
                    <SelectItem value="sports-arena">Sports arena</SelectItem>
                    <SelectItem value="museum">Museum</SelectItem>
                    <SelectItem value="art-gallery">Art gallery</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Transportation Hub</SelectLabel>
                    <SelectItem value="airport">Airport</SelectItem>
                    <SelectItem value="bus-terminal">Bus terminal</SelectItem>
                    <SelectItem value="train-station">Train station</SelectItem>
                    <SelectItem value="subway-station">Subway station</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Residential Property</SelectLabel>
                    <SelectItem value="apartment-complex">Apartment complex</SelectItem>
                    <SelectItem value="condominium">Condominium</SelectItem>
                    <SelectItem value="homeowners-association">Homeowners association</SelectItem>
                  </SelectGroup>

                  <SelectItem value="custom">Custom Account Type</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {showCustomAccountType && (
              <div className="space-y-2">
                <Label htmlFor="custom-account-type">Custom Account Type</Label>
                <Input
                  id="custom-account-type"
                  placeholder="Enter custom account type"
                  value={customAccountType}
                  onChange={(e) => setCustomAccountType(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleSelectChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button className="w-full" onClick={handleSubmit} disabled={isGeocoding}>
              {isGeocoding ? "Geocoding..." : "Update Client"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
